import {
    createNotification,
    getNotificationsForUser,
    getAllNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    archiveNotification,
    deleteNotification,
    hardDeleteNotification,
} from '../lib/db/operations';
import type { ApiResponse } from './users';
import type { Notification } from '../lib/db/schema';

export class NotificationsAPI {

    // Crear una notificación para un usuario (usado por admin)
    static async create(userId: string, title: string, message: string): Promise<ApiResponse<Notification>> {
        try {
            if (!userId || !title || !message) {
                return { success: false, error: 'userId, title and message are required' };
            }
            const result = await createNotification(userId, title, message);
            if (result.success) {
                return { success: true, data: result.data, message: 'Notification created' };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error creating notification:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Obtener notificaciones de un usuario (no borradas)
    static async getForUser(userId: string): Promise<ApiResponse<Notification[]>> {
        try {
            if (!userId) {
                return { success: false, error: 'userId is required' };
            }
            const result = await getNotificationsForUser(userId);
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error fetching notifications:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Obtener todas las notificaciones (admin)
    static async getAll(): Promise<ApiResponse<Notification[]>> {
        try {
            const result = await getAllNotifications();
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error fetching all notifications:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Marcar como leída
    static async markRead(id: string): Promise<ApiResponse<Notification>> {
        try {
            const result = await markNotificationRead(id);
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error marking notification as read:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Marcar todas como leídas para un usuario
    static async markAllRead(userId: string): Promise<ApiResponse> {
        try {
            const result = await markAllNotificationsRead(userId);
            if (result.success) {
                return { success: true, message: 'All notifications marked as read' };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error marking all notifications as read:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Archivar
    static async archive(id: string): Promise<ApiResponse<Notification>> {
        try {
            const result = await archiveNotification(id);
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error archiving notification:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Eliminar (soft-delete)
    static async delete(id: string): Promise<ApiResponse<Notification>> {
        try {
            const result = await deleteNotification(id);
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error deleting notification:', error);
            return { success: false, error: 'Internal server error' };
        }
    }

    // Eliminar definitivamente (hard-delete)
    static async hardDelete(id: string): Promise<ApiResponse<Notification>> {
        try {
            const result = await hardDeleteNotification(id);
            if (result.success) {
                return { success: true, data: result.data };
            }
            return { success: false, error: result.error };
        } catch (error) {
            console.error('API Error hard-deleting notification:', error);
            return { success: false, error: 'Internal server error' };
        }
    }
}
